import { ChevronDown, Check, Home, Building2, Wallet, Loader2, Plus } from "lucide-react";
import { formatMoney } from "@/formatters";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useLoan } from "@/context/LoanContext";
import { useBorrowerLoans, type BorrowerLoan } from "@/hooks/useBorrowerLoans";
import { isSwitcherVisibleStatus } from "@/lib/loanStatus";
import { useStartNewApplication } from "@/hooks/useStartNewApplication";
import { cn } from "@/lib/utils";
import { useState } from "react";

const iconMap: Record<string, typeof Home> = {
  Purchase: Home,
  Refinance: Building2,
  "Cash-Out Refi": Building2,
  HELOC: Wallet,
};

// NP-657 (AC-5): what a field Encompass did not supply shows as.
const MISSING_FIELD = "—";

const isEncompass = (l: BorrowerLoan) => l.losType === "Encompass";

interface LoanSwitcherProps {
  className?: string;
}

export const LoanSwitcher = ({ className }: LoanSwitcherProps = {}) => {
  const { loanId, setActiveLoan } = useLoan();
  const { data, isLoading } = useBorrowerLoans();
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);

  // NP-231 visibility gate, moved from Header (NP-70): only in-progress-or-
  // beyond loans surface in the dropdown; with none, the CTA below renders.
  const loans = (data?.loans ?? []).filter((l) => isSwitcherVisibleStatus(l.status));

  // NP-70: "+Start a New Loan" — shared action (NP-416 reuses it on the
  // Contacts LO card). Reinstated per NP-70 QA 2026-07-22; supersedes the
  // NP-178 AC19 removal.
  const startNewApplication = useStartNewApplication();
  const handleStartNewLoan = () => {
    setIsMobileSheetOpen(false);
    startNewApplication();
  };

  // Loading state renders a spinner placeholder
  if (isLoading) {
    return (
      <div className="hidden md:flex items-center gap-2 px-3 h-10 rounded-lg border border-border bg-muted/60 text-sm">
        <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Query errored or hasn't produced data (React Query leaves data undefined
  // on error): render nothing, as pre-NP-70 — the CTA below is only for a
  // confirmed 0-visible-loans state.
  if (!data) return null;

  // NP-421 (Issue 1, supersedes NP-70's 0-loans CTA): with no visible loans
  // the selector area renders NOTHING — no "Start an Application" button.
  // The "+ Start a New Loan" footer inside the dropdown/sheet remains for
  // borrowers who DO have loans.
  if (loans.length === 0) return null;

  // NP-309: with an existing-loans borrower, `loanId` only goes undefined while a
  // "Start a New Loan" intent is pending (auto-select is suppressed for exactly
  // that reason — see LoanContext's `pendingAutoSelectLoanId`). Falling back to
  // `loans[0]` in that case is what showed the OLD loan's number in the header
  // after the borrower asked to start a new one — a missing `loanId` must render
  // the transitional "New Loan Application" state instead of guessing a loan.
  //
  // A SET `loanId` that isn't (yet) in the fetched list is a different, pre-existing
  // case — e.g. the switcher's list query resolving before LoanContext's own
  // selection settles — and keeps the prior fallback rather than misreporting a
  // real selection as "New Loan Application".
  const isNewLoanState = loanId === undefined;
  const active = isNewLoanState ? undefined : loans.find((l) => l.loanId === loanId) ?? loans[0];

  const toneClass = (t: BorrowerLoan["statusTone"]) =>
    t === "success"
      ? "bg-success/15 text-success"
      : t === "warning"
      ? "bg-warning/15 text-warning"
      : "bg-info text-info-foreground dark:bg-info/20 dark:text-info";

  // NP-240: amount is null until the loan's next wizard save — show "—", not a
  // misleading $0 (NP-231 edge case: "amount shows '-'").
  // NP-597 (AC-12): the shared formatter, so a pending amount reads "TBD".
  const fmt$ = (n?: number | null) => formatMoney(n);

  // NP-240: "Multi Loan" / ["John Doe","Jane Doe"] → "John Doe & Jane Doe".
  const namesLabel = (l: BorrowerLoan) =>
    (l.borrowerNames ?? []).join(" & ") || (isEncompass(l) ? MISSING_FIELD : "");

  // NP-178 (AC1/AC2/AC4): show the Vesta loan number as the primary identifier;
  // unsubmitted loans show "Draft Application". The loans list returns
  // loanNumber = null pre-submit (NP-149 AC20–AC22 concealment), so a draft can
  // never leak a number — NP-178's label supersedes NP-149's interim
  // "In progress" placeholder. PATH-XXXX references are gone (AC6).
  // NP-657 (AC-5): an Encompass loan is never a draft, so a missing number is a gap in
  // Encompass's data rather than a loan still being filled in.
  const loanLabel = (l: BorrowerLoan) =>
    l.loanNumber ? `#${l.loanNumber}` : isEncompass(l) ? MISSING_FIELD : "Draft Application";
  // NP-815 replaces NP-178 AC5's Vesta-only "No address": both LOS read "Address not available".
  const addressLabel = (l: BorrowerLoan) => l.propertyAddress?.trim() || "Address not available";
  const loanTypeLabel = (l: BorrowerLoan) => l.loanType || (isEncompass(l) ? MISSING_FIELD : "");
  // NP-597's "TBD" means a Vesta amount not entered yet; Encompass has no such pending state.
  // NP-779: Field 2 is a calculated total, so a loan with no amount in Encompass arrives as 0
  // rather than null — and no real loan is for $0, so both read as missing.
  const isMissingEncompassAmount = (l: BorrowerLoan) =>
    isEncompass(l) && (l.loanAmount === null || l.loanAmount === 0);
  const amountLabel = (l: BorrowerLoan) =>
    isMissingEncompassAmount(l) ? MISSING_FIELD : fmt$(l.loanAmount);

  const handleLoanSelect = (selectedLoanId: string) => {
    if (selectedLoanId !== loanId) {
      // `explicit`: this is the borrower choosing, which withdraws a pending
      // "Start a New Loan" — without it the pick is discarded on the next load.
      setActiveLoan(selectedLoanId, { explicit: true });
    }
    setIsMobileSheetOpen(false);
  };

  // NP-70 (QA 2026-07-22): "+Start a New Loan" is back — NP-70's CTA spec
  // supersedes the NP-178 AC19 removal. handleStartNewLoan above clears the
  // wizard store + draft + active loan and lands on the dashboard.

  const renderLoanList = (isMobile = false) => (
    <div className={cn("space-y-0.5", isMobile ? "py-2" : "py-1")}>
      {/* NP-309 (AC-5): while a "Start a New Loan" reset is pending, surface it as
          the active entry alongside the existing loans — not a separate call to
          action, since one already exists below for triggering it. */}
      {isNewLoanState && (
        <div
          className={cn(
            "w-full flex items-start gap-3 rounded-md px-2 py-2.5 bg-muted",
            isMobile && "min-h-[60px]"
          )}
          data-testid={`loan-switcher-${isMobile ? 'mobile' : 'desktop'}-new-loan-entry`}
        >
          <div className="h-9 w-9 shrink-0 rounded-lg bg-accent-soft text-gold grid place-items-center">
            <Plus className="size-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[13px] truncate">New Loan Application</span>
              <Check className="size-3.5 text-primary shrink-0" />
            </div>
            <div className="text-[11px] text-muted-foreground truncate">Getting started</div>
          </div>
        </div>
      )}
      {loans.map((l) => {
        const Icon = iconMap[l.loanType] ?? Home;
        const isActive = l.loanId === loanId;
        return (
          <button
            key={l.loanId}
            onClick={() => handleLoanSelect(l.loanId)}
            className={cn(
              "w-full flex items-start gap-3 rounded-md px-2 py-2.5 text-left transition-colors focus:bg-muted focus:text-foreground hover:bg-muted hover:text-foreground dark:focus:bg-muted dark:hover:bg-muted",
              isActive && "bg-muted",
              isMobile && "min-h-[60px] touch-manipulation"
            )}
            data-testid={`loan-switcher-${isMobile ? 'mobile' : 'desktop'}-option-${l.loanId}`}
          >
            <div className="h-9 w-9 shrink-0 rounded-lg bg-accent-soft text-gold grid place-items-center">
              <Icon className="size-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[13px] truncate">{loanLabel(l)}</span>
                {isActive && <Check className="size-3.5 text-primary shrink-0" />}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{addressLabel(l)}</div>
              {/* NP-240: borrower name(s). NP-798 dropped the " · 2d ago" last-updated suffix. */}
              {namesLabel(l) && (
                <div className="text-[11px] text-muted-foreground truncate">{namesLabel(l)}</div>
              )}
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[11px] font-medium text-foreground/80">{loanTypeLabel(l)}</span>
                <span className="text-[10px] text-muted-foreground">·</span>
                <span className="text-[11px] font-semibold text-foreground/90">{amountLabel(l)}</span>
                <span
                  className={cn(
                    "ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-medium",
                    toneClass(l.statusTone)
                  )}
                >
                  {/* NP-70: the badge reflects the Vesta milestone (currentStage)
                      when available — the key loan-stage field — else status. */}
                  {l.milestone || l.status}
                </span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      {/* Desktop Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn("group hidden md:flex items-center gap-3 px-3 h-10 rounded-lg border border-border bg-muted/60 text-foreground text-sm hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background", className)}
            data-testid="loan-switcher-desktop-trigger"
          >
            <div className="text-left leading-tight">
              {/* NP-309 (AC-2): before Vesta assigns the new loan a number, the
                  badge reads "New Loan Application" — no loan number, no milestone. */}
              {active ? (
                <>
                  <div className="font-semibold text-[13px]">{loanLabel(active)}</div>
                  <div className="text-[10.5px] text-muted-foreground">{active.loanType}{active.milestone ? ` · ${active.milestone}` : ""}</div>
                </>
              ) : (
                <div className="font-semibold text-[13px]">New Loan Application</div>
              )}
            </div>
            {/* NP-70: chevron rotates 180° when the dropdown is open. */}
            <ChevronDown className="size-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-[340px] p-1.5" data-testid="loan-switcher-desktop-dropdown">
          <DropdownMenuLabel className="flex items-center justify-between px-2 py-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Your Loans
            </span>
            <span className="text-[11px] font-medium text-muted-foreground">{loans.length} active</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* NP-178 (AC13–AC15): show at most 3 loans, then scroll. */}
          <div className="max-h-[228px] overflow-y-auto">
            {renderLoanList(false)}
          </div>

          {/* NP-70: "+Start a New Loan" must always be available in the
              dropdown (reinstated per QA 2026-07-22; supersedes NP-178 AC19). */}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={handleStartNewLoan}
            className="px-2 py-2 text-[13px] font-medium text-primary focus:text-primary cursor-pointer focus:bg-muted hover:bg-muted hover:text-primary dark:focus:bg-muted dark:hover:bg-muted"
            data-testid="loan-switcher-desktop-new-application"
          >
            <Plus className="size-4 mr-2" /> Start a New Loan
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Mobile Bottom Sheet */}
      <Sheet open={isMobileSheetOpen} onOpenChange={setIsMobileSheetOpen}>
        <SheetTrigger asChild>
          <button
            className={cn("group md:hidden flex flex-nowrap items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/60 text-foreground text-sm hover:bg-muted transition-colors touch-manipulation min-w-0", className)}
            data-testid="loan-switcher-mobile-trigger"
          >
            {/* NP-500: min-w-0 + truncate so this yields width instead of forcing the
                header onto a second row. It is the widest thing in the row and the
                only one that can give — the logo, hamburger and avatar are all
                fixed 44px touch targets. */}
            <div className="text-left leading-tight min-w-0">
              {active ? (
                <>
                  <div className="font-semibold text-[13px] truncate">{loanLabel(active)}</div>
                  <div className="text-[10.5px] text-muted-foreground truncate">{active.loanType}{active.milestone ? ` · ${active.milestone}` : ""}</div>
                </>
              ) : (
                <div className="font-semibold text-[13px] truncate">New Loan Application</div>
              )}
            </div>
            <ChevronDown className="size-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </button>
        </SheetTrigger>

        <SheetContent side="bottom" className="h-[70vh] max-h-[600px]" data-testid="loan-switcher-mobile-sheet">
          <SheetHeader className="pb-4">
            <SheetTitle className="flex items-center justify-between text-left">
              <span>Your Loans</span>
              <span className="text-sm font-medium text-muted-foreground">{loans.length} active</span>
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-col">
            {/* NP-178 (AC13/AC16): show at most 3 loans, then scroll. */}
            <div className="max-h-[264px] overflow-y-auto">
              {renderLoanList(true)}
            </div>

            {/* NP-70: "+Start a New Loan" — always available (see desktop note). */}
            <div className="mt-4 pt-4 border-t border-border">
              <button
                onClick={handleStartNewLoan}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary/10 text-primary font-medium text-sm touch-manipulation transition-colors hover:bg-primary/15"
                data-testid="loan-switcher-mobile-new-application"
              >
                <Plus className="size-4" /> Start a New Loan
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
